import { z } from "zod";
import type { GleanCollectedContext } from "../glean/contextCollector";
import type { FetchScopedOpts } from "../state/useScopeData";
import { classifyFetchError } from "../state/denialErrors";

type ScopedFetch = <T>(path: string, opts?: FetchScopedOpts) => Promise<T>;

const preparedSchema = z.object({
  job_id: z.string(), generated_prompt: z.string().max(16_000), scope_summary: z.string().min(1),
  source_references: z.array(z.string()), warnings: z.array(z.string()),
  requires_clarification: z.boolean(), clarification_question: z.string().nullable(),
  expires_at: z.string().datetime({ offset: true }).nullable().optional(),
}).superRefine((value, ctx) => {
  if (value.requires_clarification) {
    if (!value.clarification_question?.trim()) ctx.addIssue({ code: "custom", message: "Clarification question is missing" });
  } else if (!value.generated_prompt.trim() || !value.job_id) {
    ctx.addIssue({ code: "custom", message: "Prepared prompt is incomplete" });
  }
});
const jobSchema = z.object({
  job_id: z.string(), status: z.enum(["prepared", "awaiting_review", "waiting_helper", "claimed", "opening_glean", "sign_in_required", "submitting", "submitted", "uncertain", "failed", "expired", "cancelled"]), scope_summary: z.string(),
  source_references: z.array(z.string()), warnings: z.array(z.string()),
  helper_online: z.boolean(), expires_at: z.string().datetime({ offset: true }),
  failure_code: z.string().max(64).nullable().optional(),
});

const installerSchema = z.string().max(2_048).refine((value) => {
  if (/[\s\\]/.test(value)) return false;
  if (/^\/(?!\/)/.test(value) && !value.includes("\\")) return true;
  try { const url = new URL(value); return url.protocol === "https:" && !url.username && !url.password; } catch { return false; }
}, "Invalid installation address").nullable();
const installerUrlsSchema = z.object({ windows: installerSchema, macos: installerSchema, linux: installerSchema });

const launchUrlSchema = z.string().max(8_192).refine((value) => {
  if (/[\s\\]/.test(value)) return false;
  try {
    const url = new URL(value);
    return url.protocol === "velocity-glean:" && url.hostname === "submit"
      && !url.username && !url.password && !url.port && !url.hash
      && (url.pathname === "" || url.pathname === "/")
      && [...url.searchParams.keys()].length === 1
      && /^[A-Za-z0-9_.-]{20,7000}$/.test(url.searchParams.get("job") ?? "");
  } catch { return false; }
}, "Invalid automation launch");

const configSchema = z.object({
  glean_url: z.string().max(2_048).refine((value) => {
    if (/[\s\\]/.test(value)) return false;
    try {
      const url = new URL(value);
      return url.protocol === "https:" && url.hostname === "app.glean.com" && !url.username && !url.password
        && !url.port && !url.search && !url.hash;
    } catch { return false; }
  }, "Invalid Glean address"), helper_protocol: z.literal("velocity-glean"),
  helper_install_url: installerSchema, launch_timeout_seconds: z.number().int().min(5).max(120),
  helper_install_urls: installerUrlsSchema.optional(),
  helper_install_status: z.enum(["resolved", "retryable_error", "error"]).optional(),
  helper_install_retry_after_seconds: z.number().int().min(1).max(30).optional(),
});
const launchSchema = jobSchema.extend({
  launch_url: launchUrlSchema.nullable(), review_id: z.string().min(1),
  launch_expires_at: z.string().datetime({ offset: true }).optional(),
  helper_install_url: installerSchema.optional(),
  helper_install_urls: installerUrlsSchema.optional(),
});
const reviewSchema = z.object({
  review_id: z.string().min(1), job_id: z.string().min(1), generated_prompt: z.string().trim().min(1).max(16_000),
  scope_summary: z.string(), source_references: z.array(z.string()), warnings: z.array(z.string()),
  expires_at: z.string().datetime({ offset: true }), approved: z.boolean(),
  device_label: z.string().max(120).nullable(), helper_install_url: installerSchema.optional(),
  helper_install_urls: installerUrlsSchema.optional(),
});

export type PreparedGleanPrompt = z.infer<typeof preparedSchema>;
export type GleanJob = z.infer<typeof jobSchema>;
export type GleanLaunch = z.infer<typeof launchSchema>;
export type GleanReview = z.infer<typeof reviewSchema>;
export type GleanHelperConfig = z.infer<typeof configSchema>;
export type GleanInstallerConfig = Pick<GleanHelperConfig, "helper_install_urls"> & { helper_install_url?: string | null };

function handoffId(value: string): string {
  return z.string().regex(/^[A-Za-z0-9_-]{1,128}$/).parse(value);
}

async function requestHandoff(path: string, init?: RequestInit): Promise<unknown> {
  const response = await fetch(`/api/workspace/glean-handoff${path}`, { ...init, credentials: "include" });
  if (!response.ok) throw await classifyFetchError(response, "Send to Glean");
  return response.json();
}

export async function getGleanHelperConfig(signal?: AbortSignal): Promise<GleanHelperConfig> {
  return configSchema.parse(await requestHandoff("/config", { signal }));
}

export function openGleanHelper(launchUrl: string): void {
  window.location.assign(launchUrlSchema.parse(launchUrl));
}

export async function launchGleanJob(fetchScoped: ScopedFetch, jobId: string, prompt: string): Promise<GleanLaunch> {
  const approvedPrompt = z.string().trim().min(1).max(16_000).parse(prompt);
  const result = launchSchema.parse(await fetchScoped<unknown>(`/glean-handoff/jobs/${handoffId(jobId)}/launch`, {
    init: { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ prompt: approvedPrompt }) },
  }));
  if (result.job_id !== jobId) throw new Error("Unexpected handoff response");
  return result;
}

export async function getGleanReview(reviewId: string, signal?: AbortSignal): Promise<GleanReview> {
  const result = reviewSchema.parse(await requestHandoff(`/launch/reviews/${handoffId(reviewId)}`, { signal }));
  if (result.review_id !== reviewId) throw new Error("Unexpected review response");
  return result;
}

export async function approveGleanReview(reviewId: string, prompt: string): Promise<GleanJob> {
  const approvedPrompt = z.string().trim().min(1).max(16_000).parse(prompt);
  const result = jobSchema.parse(await requestHandoff(`/launch/reviews/${handoffId(reviewId)}/approve`, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ prompt: approvedPrompt }),
  }));
  if (result.job_id !== reviewId) throw new Error("Unexpected approval response");
  if (["prepared", "awaiting_review"].includes(result.status)) throw new Error("Approval was not confirmed");
  return result;
}

export async function getGleanReviewJob(jobId: string): Promise<GleanJob> {
  const result = jobSchema.parse(await requestHandoff(`/jobs/${handoffId(jobId)}`));
  if (result.job_id !== jobId) throw new Error("Unexpected handoff response");
  return result;
}

export async function cancelGleanReviewJob(jobId: string): Promise<GleanJob> {
  const result = jobSchema.parse(await requestHandoff(`/jobs/${handoffId(jobId)}/cancel`, { method: "POST" }));
  if (result.job_id !== jobId) throw new Error("Unexpected handoff response");
  return result;
}

export async function prepareGleanPrompt(fetchScoped: ScopedFetch, context: GleanCollectedContext, signal?: AbortSignal) {
  return preparedSchema.parse(await fetchScoped<unknown>("/glean-handoff/jobs/prepare", {
    init: { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ context }), signal },
  }));
}

export async function dispatchGleanJob(fetchScoped: ScopedFetch, jobId: string, prompt: string) {
  const approvedPrompt = z.string().trim().min(1).max(16_000).parse(prompt);
  return jobSchema.parse(await fetchScoped<unknown>(`/glean-handoff/jobs/${jobId}/dispatch`, {
    init: { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ prompt: approvedPrompt }) },
  }));
}

export async function getGleanJob(fetchScoped: ScopedFetch, jobId: string) {
  const result = jobSchema.parse(await fetchScoped<unknown>(`/glean-handoff/jobs/${handoffId(jobId)}`));
  if (result.job_id !== jobId) throw new Error("Unexpected handoff response");
  return result;
}

export async function cancelGleanJob(fetchScoped: ScopedFetch, jobId: string) {
  const result = jobSchema.parse(await fetchScoped<unknown>(`/glean-handoff/jobs/${handoffId(jobId)}/cancel`, { init: { method: "POST" } }));
  if (result.job_id !== jobId) throw new Error("Unexpected handoff response");
  return result;
}
